/**
 * `<ActiveSpans>` — section 4 of the B-1 VM Debug pane.
 *
 * Shows the set of currently in-flight outbound requests — anything
 * we've sent (`captureSent`) but for which we haven't seen a terminal
 * response (`captureReceived` with `isStreamChunk: false` OR
 * `captureStreamEnd`).
 *
 * Pure derivation: takes the buffered message list, walks it, and
 * tracks which `(method, requestIdSurrogate)` pairs have an open sent
 * with no matching terminal received. The list is bounded by the
 * concurrent-in-flight count; in practice this is tiny (1-3 outbound
 * Anthropic SDK calls at most).
 *
 * Click-through-to-Datadog is **out of scope for B-1** (TDD §B-1
 * "Defer to B-2 / follow-ups"). The row renders a link-styled span
 * with the Datadog URL but no in-pane click handler.
 */

import { useMemo } from 'react';
import type { VmMessage } from '@volley/crucible-ipc-types/vm-message-stream';

export interface ActiveSpan {
  /** Surrogate hash of the request id — the join key the user pastes
   *  into Datadog. */
  requestIdSurrogate: string;
  /** Method the request was issued for. */
  method: string;
  /** ISO-8601 of the sent capture. */
  sentTs: string;
  /** Seq of the sent capture. */
  sentSeq: number;
}

export interface ActiveSpansProps {
  messages: VmMessage[];
}

/** Pure walker — exported for unit tests so we can pin behaviour
 *  without rendering the component. */
export function deriveActiveSpans(messages: VmMessage[]): ActiveSpan[] {
  // Map of `${method}:${requestIdSurrogate}` → ActiveSpan for the
  // sent capture. Removed when a terminal received (response /
  // stream-end) arrives for the same key.
  const open = new Map<string, ActiveSpan>();
  for (const msg of messages) {
    // Only requests with a meaningful surrogate participate. A blank
    // surrogate (e.g. lifecycle, heartbeat) can never be matched
    // back to a terminal.
    if (!msg.requestIdSurrogate) continue;
    const key = `${msg.method}:${msg.requestIdSurrogate}`;
    if (msg.direction === 'sent' && msg.kind === 'request') {
      open.set(key, {
        requestIdSurrogate: msg.requestIdSurrogate,
        method: msg.method,
        sentTs: msg.ts,
        sentSeq: msg.seq,
      });
    } else if (
      msg.direction === 'received' &&
      (msg.kind === 'response' || msg.kind === 'stream-end')
    ) {
      open.delete(key);
    }
    // Stream-chunks are non-terminal; they neither open nor close
    // an active span. Heartbeat / lifecycle / audit-emit are
    // already filtered out by the blank-surrogate check above.
  }
  // Stable sort by sentSeq ascending so the rendered order matches
  // dispatch order (oldest at the top, newest at the bottom).
  return [...open.values()].sort((a, b) => a.sentSeq - b.sentSeq);
}

export function ActiveSpans({ messages }: ActiveSpansProps): React.JSX.Element {
  const spans = useMemo(() => deriveActiveSpans(messages), [messages]);
  return (
    <div
      data-testid="vm-debug-active-spans"
      className="sticky bottom-[var(--cap-cohort-h,32px)] z-10 border-t border-white/10 bg-black/40 px-3 py-2 text-[10px]"
    >
      <div className="mb-1 text-white/40 uppercase">Active spans ({spans.length})</div>
      {spans.length === 0 ? (
        <div className="text-white/30">No in-flight requests.</div>
      ) : (
        <ul className="space-y-0.5">
          {spans.map((span) => (
            <li
              key={`${span.method}:${span.requestIdSurrogate}`}
              data-testid="vm-debug-active-span"
              data-method={span.method}
              data-request-id-surrogate={span.requestIdSurrogate}
              className="flex items-center gap-2 font-mono text-white/70"
            >
              <span>{span.method}</span>
              <span className="text-white/40">req_{span.requestIdSurrogate}</span>
              <span className="text-white/30">{span.sentTs}</span>
              {/* Datadog link is intentionally rendered without an
                  href (no in-pane navigation in B-1 — TDD §B-1 defer).
                  The surrogate IS the join key the operator pastes
                  into a Datadog query. */}
              <span className="ml-auto text-white/30 italic">paste req_ into Datadog</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
